import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Leaf, Heart, Shield } from 'lucide-react';

export const Hero = () => {
  const scrollToProducts = () => {
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: '#f5e9d0' }}
    >
      <div
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1531969179221-3946e6b5a5e7)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* Floating tea leaves decoration */}
      <div className="absolute top-20 left-10 text-6xl opacity-20 animate-float" style={{ animationDelay: '0s' }}>🍃</div>
      <div className="absolute top-40 right-20 text-4xl opacity-15 animate-float" style={{ animationDelay: '1s' }}>🌿</div>
      <div className="absolute bottom-40 left-20 text-5xl opacity-15 animate-float" style={{ animationDelay: '0.5s' }}>🍂</div>
      <div className="absolute bottom-60 right-10 text-3xl opacity-20 animate-float" style={{ animationDelay: '1.5s' }}>🍃</div>
      
      {/* Decorative root pattern */}
      <div className="absolute bottom-0 left-0 right-0 h-32 opacity-10"
        style={{
          backgroundImage: 'linear-gradient(to top, #5e3a1c, transparent)',
        }}
      />
      
      <div className="container mx-auto px-6 relative z-10 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 animate-fade-in">
            <img 
              src="https://customer-assets.emergentagent.com/job_leaf-lounge-3/artifacts/kbshdl1e_file_00000000646471fdbb03789bab2f6e30.png" 
              alt="Mudbutt Brew"
              className="w-64 md:w-80 mx-auto animate-float"
              style={{ animationDuration: '4s' }}
            />
          </div>
          
          <h1
            className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up"
            style={{ 
              color: '#5e3a1c',
              lineHeight: '1.2',
              fontFamily: 'Georgia, serif',
              animationDelay: '0.2s',
              animationFillMode: 'both'
            }}
          >
            Rooted in Tradition,
            <br />
            <span style={{ color: '#c2a357' }}>Crafted with Nature</span>
          </h1>
          
          <p
            className="text-2xl md:text-3xl font-bold mb-6 italic animate-fade-in-up"
            style={{ color: '#8b5c2f', animationDelay: '0.4s', animationFillMode: 'both' }}
          >
            "From the mud to your mug—fuel for the fight."
          </p>
          
          <p
            className="text-base md:text-lg mb-8 max-w-3xl mx-auto leading-relaxed animate-fade-in-up"
            style={{ color: '#5e3a1c', animationDelay: '0.6s', animationFillMode: 'both' }}
          >
            <strong>Mud Butt Brew:</strong> Born in the backwoods of Elma, Washington—where the rain never quits, 
            the trails get muddy, and the tea keeps you upright. We're not fancy; we're functional. 
            Hand-blended herbs for real folks who need a gut reset, immune punch, or just something hot 
            after a long hike. No bullshit additives—just roots, leaves, and a little grit. 
            Brew it strong, sip it slow. Your body's gonna thank you... eventually.
          </p>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 mb-8 animate-fade-in-up" style={{ animationDelay: '0.8s', animationFillMode: 'both' }}>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: 'rgba(94, 58, 28, 0.1)' }}>
              <Leaf className="w-5 h-5" style={{ color: '#3a692b' }} />
              <span className="text-sm font-bold" style={{ color: '#5e3a1c' }}>100% Natural</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: 'rgba(94, 58, 28, 0.1)' }}>
              <Heart className="w-5 h-5" style={{ color: '#8b5c2f' }} />
              <span className="text-sm font-bold" style={{ color: '#5e3a1c' }}>Hand-Crafted</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: 'rgba(94, 58, 28, 0.1)' }}>
              <Shield className="w-5 h-5" style={{ color: '#c2a357' }} />
              <span className="text-sm font-bold" style={{ color: '#5e3a1c' }}>Small Batch</span>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up" style={{ animationDelay: '1s', animationFillMode: 'both' }}>
            <Button
              onClick={scrollToProducts}
              size="lg"
              className="text-lg px-8 py-6 group animate-pulse-glow"
              style={{ backgroundColor: '#8b5c2f', color: '#f5e9d0' }}
            >
              Explore Our Brews
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 rounded-full flex items-start justify-center p-2" style={{ borderColor: '#8b5c2f' }}>
          <div className="w-1 h-3 rounded-full" style={{ backgroundColor: '#8b5c2f' }} />
        </div>
      </div>
    </section>
  );
};
